import { Fragment, useEffect, useRef, useState } from "react";
import { CircleMarker, Polyline, Popup } from "react-leaflet";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// Actions
import { openTabContentPreviewForm } from "../../actions/campaign-planning/TabContentPreviewFormActions";
import {
  addInfluenceCircleMarkers,
  removeInfluenceCircleMarkers,
  setInfluenceCircleRadius,
} from "../../../actions/map-view/poi/PoiSelectionActions";

// Constants And Utils
import {
  India,
  MediaTypeCategories,
  MediaTypeCategoriesColors,
  RoadStretchesPathOptionsConstants,
} from "../../../constants/GeneralConstants";
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";
import { toStringWithRoundUp } from "../../../common-utils/number-utils/NumberUtils";
import { StretchSelectionButton } from "./PlanningUtils";
import { ProohFormDataTargets } from "../../constants/GeneralConstants";
import { constructTgHeatmapKey } from "../../utils/campaignPlanningUtil";
import {
  getImpFilteredStretches,
  getPlanningQueryParams,
  getRoadStretchesOrgMedias,
  getSelectedFreqOptLocations,
} from "./CampaignPlanningUtils";

// Components
import LLMap from "../../../components/map/leaflet-map/LLMap";
import TrafficFlowArrow from "../../../components/map/traffic-flow-arrow/TrafficFlowArrow";
import TargetGroupHeatmap from "../../../components/map/target-group-heatmap/TargetGroupHeatmap";
import PoiMarkers from "../../../components/map/markers/PoiMarkers";
import MapZoomIndicator from "../../../components/map/map-zoom-indicator/MapZoomIndicator";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import { MediaImage } from "../../../components/campaign-media-table-row/MediaRow";
import DataLayerMarkers from "../../../pages/map-view/data-layers/DataLayerMarkers";

// Section Component
function StretchTooltipMessages({
  stretchName,
  tgSpecificImpressions,
  estImpressions,
}) {
  // Selected TgId
  const tgInfo = useSelector((state) => state.orgTargetGroup.tgInfo);
  const { targetGroup } = tgInfo || {};
  const { id: selectedTgId } = targetGroup || {};

  const formattedEstImp = formatText(toLocaleString(estImpressions));
  const formattedTgImp = formatText(toLocaleString(tgSpecificImpressions));

  // Showing Tg "TG Imp" and "Generic Imp" impressions (if present)
  return (
    <div>
      <b className="d-block mb-1">{stretchName}</b>
      {selectedTgId && (
        <span>
          {"Imp TG: "}
          <b>{formattedTgImp}</b>
        </span>
      )}
      <span className="d-block">
        {"Imp Gen: "}
        <b>{formattedEstImp}</b>
      </span>
    </div>
  );
}

// keep the same order :: selected, fol, touchPoint, tg, unselected
function getPathOptions(
  stretchSelected,
  isSelectedFolStretch,
  isSelectedTouchPointsStretch,
  isTopFiveTgImpStretch
) {
  // path options constants
  const { selected, unSelected, freqOptLoc, touchPoint, targetGroup } =
    RoadStretchesPathOptionsConstants;

  // selected stretch
  if (stretchSelected) {
    return selected;
  }

  // fol
  if (isSelectedFolStretch) {
    return freqOptLoc;
  }

  // touch point
  if (isSelectedTouchPointsStretch) {
    return touchPoint;
  }

  // tg
  if (isTopFiveTgImpStretch) {
    return targetGroup;
  }

  // default colored
  return unSelected;
}

// Page Components
function StretchLine({
  eachStretch,
  duration,
  selectedTouchPoints = [],
  selectedFOL = [],
  sortedTopFiveStretchesIds = [],
}) {
  const polylineRef = useRef();

  // Hovered Stretch Id
  const hoveredStretchId = useSelector(
    (state) => state.campaignPlanning.hoveredStretchId
  );

  const {
    otsAvg,
    otsLitAvg,
    targetOts,
    targetOtsLit,
    trace,
    id,
    name,
    metaData = {},
    targetGroupId,
  } = eachStretch || {};
  const touchPoints = metaData?.touchPoints || [];
  const locationName = metaData?.locationName || "";

  // checking association for TouchPoints
  const isSelectedTouchPointsStretch = touchPoints.some((tp) =>
    selectedTouchPoints.includes(tp)
  );

  // checking association for FOL
  const isSelectedFolStretch = selectedFOL.includes(locationName) || false;

  // for different color of top 5 tgImpressions stretches
  const isTopFiveTgImpStretch =
    targetGroupId && sortedTopFiveStretchesIds.includes(id);

  // Without selecting TG
  const estImpressions = toStringWithRoundUp(duration * (otsLitAvg ?? otsAvg));

  // With Selecting TG
  const tgSpecificImpressions = toStringWithRoundUp(
    duration * (targetOtsLit ?? targetOts)
  );

  // Selected Road-Stretch
  const stretchSelected = useSelector(
    (state) => state.planningRoadStretches.selectedStretches[id]
  );

  // path options
  const pathOptions = getPathOptions(
    stretchSelected,
    isSelectedFolStretch,
    isSelectedTouchPointsStretch,
    isTopFiveTgImpStretch
  );

  // conditionally showing popup when stretch is hovered
  useEffect(() => {
    if (hoveredStretchId === id) {
      polylineRef.current.openPopup();
      return;
    }
    polylineRef.current.closePopup();
  }, [hoveredStretchId]);

  return (
    <Polyline ref={polylineRef} pathOptions={pathOptions} positions={trace}>
      <Popup>
        <StretchTooltipMessages
          stretchName={name}
          tgSpecificImpressions={tgSpecificImpressions}
          estImpressions={estImpressions}
        />
        <div className="mt-2 d-flex justify-content-center">
          <StretchSelectionButton stretchInfo={eachStretch} />
        </div>
      </Popup>
    </Polyline>
  );
}

function PoiInfluenceCircleBtn() {
  const dispatch = useDispatch();

  const showPoiInfluenceCircle = useSelector(
    (state) => state.poiSelection.showInfluenceCircle
  ); // true/false

  const influenceCircleRadius = useSelector(
    (state) => state.poiSelection.influenceCircleRadius
  );

  // Title
  const title = showPoiInfluenceCircle
    ? "Hide Poi Influence Circle"
    : "Show Poi Influence Circle";

  // Radius constants
  const radiusConstants = [
    { id: 500, label: "500 Metres" },
    { id: 1000, label: "1 KM" },
    { id: 2000, label: "2 KM" },
    { id: 3000, label: "3 KM" },
    { id: 4000, label: "4 KM" },
    { id: 5000, label: "5 KM" },
  ];

  function toggleInfluenceCircleMarkers() {
    const action = showPoiInfluenceCircle
      ? removeInfluenceCircleMarkers
      : addInfluenceCircleMarkers;
    dispatch(action());
  }

  const showCircleIconClass = showPoiInfluenceCircle
    ? "text-primary"
    : "text-muted";

  return (
    <div className="d-flex align-items-center bg-white rounded-lg p-2 ml-2">
      <button
        className={"btn shadow-none p-0"}
        onClick={toggleInfluenceCircleMarkers}
        title={title}
      >
        <i
          className={`fas fa-map-marker-alt mx-1 fs-8 ${showCircleIconClass}`}
        ></i>
      </button>

      {/* Radius selector */}
      {showPoiInfluenceCircle && (
        <PlainDropdown
          items={radiusConstants}
          onItemSelect={(radius) => dispatch(setInfluenceCircleRadius(radius))}
          className="border-0 ml-1"
          textClassName="text-primary h5 mb-0"
          selectedId={influenceCircleRadius}
        />
      )}
    </div>
  );
}

function MediaMarkerShowHideBtn({
  showStaticMediaMarkers,
  setShowStaticMediaMarkers = () => {},
  showDigitalMediaMarkers,
  setShowDigitalMediaMarkers = () => {},
}) {
  const { search } = useLocation();
  const { isPureDooh } = getPlanningQueryParams(search);

  // btn data
  const buttonData = [
    {
      icon: "fa-image",
      mediaType: MediaTypeCategories.static,
      isMarkerVisible: showStaticMediaMarkers,
      onClick: () => setShowStaticMediaMarkers(!showStaticMediaMarkers),
      isHideBtn: isPureDooh,
    },
    {
      icon: "fa-desktop",
      mediaType: MediaTypeCategories.digital,
      isMarkerVisible: showDigitalMediaMarkers,
      onClick: () => setShowDigitalMediaMarkers(!showDigitalMediaMarkers),
      isHideBtn: false,
    },
  ];

  return (
    <>
      {buttonData.map((data, i) => {
        const { icon, onClick, isMarkerVisible, mediaType, isHideBtn } = data;
        const iconClassName = isMarkerVisible ? "text-primary" : "text-muted";
        const title = isMarkerVisible
          ? `Hide ${mediaType} Media Markers`
          : `Show ${mediaType} Media Markers`;

        // hiding btn condition
        if (isHideBtn) {
          return null;
        }

        return (
          <button
            key={i}
            className="btn shadow-none bg-white p-2 rounded-lg ml-2"
            onClick={onClick}
            title={title}
          >
            <i className={`fas fs-8 ${icon} ${iconClassName}`}></i>
          </button>
        );
      })}
    </>
  );
}

function ContentPreviewBtn() {
  const dispatch = useDispatch();

  // check if modal is open or close
  const isTabContentPreviewFormVisible = useSelector(
    (state) => state.tabContentPreviewForm.openModal
  );

  // className
  const iconClassName = isTabContentPreviewFormVisible
    ? "text-primary"
    : "text-muted";

  return (
    <button
      className="btn shadow-none bg-white p-2 rounded-lg"
      data-toggle="modal"
      data-target={`#${ProohFormDataTargets.tabContentPreviewForm}`}
      onClick={() => dispatch(openTabContentPreviewForm())}
      title="Show Selected Content Information"
    >
      <i className={`fas fa-info-circle fs-8 ${iconClassName}`}></i>
    </button>
  );
}

function SelectedPreviewAndShowMediaButton({
  showStaticMediaMarkers,
  setShowStaticMediaMarkers,
  showDigitalMediaMarkers,
  setShowDigitalMediaMarkers,
}) {
  return (
    <div className="d-flex align-items-center preview-button">
      <ContentPreviewBtn />
      <MediaMarkerShowHideBtn
        showStaticMediaMarkers={showStaticMediaMarkers}
        setShowStaticMediaMarkers={setShowStaticMediaMarkers}
        showDigitalMediaMarkers={showDigitalMediaMarkers}
        setShowDigitalMediaMarkers={setShowDigitalMediaMarkers}
      />
      <PoiInfluenceCircleBtn />
    </div>
  );
}

function MediaPopupContent({ media }) {
  const { title, isDigital, sourceId, height, width, mediaTag } = media || {};

  return (
    <Popup>
      <div className="d-flex">
        <MediaImage media={media} />
        <div className="col-9 pr-0">
          <p className="m-0 mb-1 font-weight-bold" title={title}>
            {title}
          </p>
          <p className="m-0">{`Height: ${height} ft | Width: ${width} ft`}</p>
          <p className="m-0">
            Device ID: <b>{sourceId}</b>
          </p>
          <p className="m-0">
            Static/Digital: <b>{isDigital ? "Digital" : "Static"}</b>
          </p>
          <p className="m-0">
            Media Type: <b>{formatText(mediaTag)}</b>
          </p>
        </div>
      </div>
    </Popup>
  );
}

// Org media Markers
function OrgMediasMarkers({ mediaList = [], isDigitalMediaMarker = false }) {
  if (mediaList.length === 0) {
    return null;
  }

  // Marker colors
  const markerFillColor = isDigitalMediaMarker
    ? MediaTypeCategoriesColors.digital
    : MediaTypeCategoriesColors.static;

  return (
    <>
      {mediaList.map((media) => {
        const { id, latitude, longitude } = media;
        return (
          <CircleMarker
            key={id}
            center={[latitude, longitude]}
            radius={7}
            fillColor={markerFillColor}
            color="black"
            weight={1}
            fillOpacity={0.9}
          >
            <MediaPopupContent media={media} />
          </CircleMarker>
        );
      })}
    </>
  );
}

/**
 * Planning MapView
 */
export default function PlanningMapView({
  roadStretchData = [],
  sortedRoadStretchesData = [], // for showing top 5 tg imp color
}) {
  const [showStaticMediaMarkers, setShowStaticMediaMarkers] = useState(false);
  const [showDigitalMediaMarkers, setShowDigitalMediaMarkers] = useState(false);

  // region data
  const regionsData = useSelector(
    (state) => state.campaignPlanning.regionsData
  );
  const { center, id: selectedCityId } = regionsData || {};

  // all poiTypeLayer points combined array ==> TG HeatMap Data
  const allPoiPointsArr = useSelector((state) => state.geoData.allPoiPointsArr);

  // selected tg attr map
  const selectedTgAttributesMap = useSelector(
    (state) => state.campaignPlanning.selectedTgAttributesMap
  );
  const targetGroupHeatmapKey = constructTgHeatmapKey(
    selectedTgAttributesMap,
    selectedCityId
  );

  // selectedTouchPoints
  const selectedTouchPoints = useSelector(
    (state) => state.campaignPlanning.selectedTouchPoints
  );

  // Selected Region & Zones Map
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );
  const selectedFOL = getSelectedFreqOptLocations(
    selectedRegionAndZonesMap,
    selectedCityId
  );

  // after sorting by imp, top 5 stretch ids
  const sortedTopFiveStretchesIds = sortedRoadStretchesData
    .map((stretch) => stretch.id)
    .slice(0, 5);

  // Campaign Plan Duration
  const duration = useSelector(
    (state) => state.campaignPlanning.durationInDays
  );

  // targetGroupId
  const tgId = useSelector((state) => state.tgSpecificOts.tgId);

  // filter road stretches by imp/tgImp
  const impFilteredStretches = getImpFilteredStretches(roadStretchData, tgId);

  // org medias for road stretches
  const { staticMedias, digitalMedias } =
    getRoadStretchesOrgMedias(impFilteredStretches);

  // Show India Map
  if (!center) {
    return (
      <div className="toohl-map-container">
        <LLMap center={India.mapCenter} zoom={India.mapZoomLevel5} />
      </div>
    );
  }

  return (
    <div className="toohl-map-container">
      <LLMap center={[center.latitude, center.longitude]} zoom={11}>
        {/* Stretches */}
        {impFilteredStretches.map((eachStretch, i) => (
          <Fragment key={i}>
            <StretchLine
              key={eachStretch.id}
              eachStretch={eachStretch}
              duration={duration}
              selectedTouchPoints={selectedTouchPoints}
              selectedFOL={selectedFOL}
              sortedTopFiveStretchesIds={sortedTopFiveStretchesIds}
            />
            <TrafficFlowArrow trace={eachStretch.trace} />
          </Fragment>
        ))}

        {/* Org Media Markers : Static */}
        {showStaticMediaMarkers && (
          <OrgMediasMarkers mediaList={staticMedias} />
        )}

        {/* Org Media Markers : Digital */}
        {showDigitalMediaMarkers && (
          <OrgMediasMarkers
            mediaList={digitalMedias}
            isDigitalMediaMarker={true}
          />
        )}

        {/* Heatmap */}
        {allPoiPointsArr.length > 0 && (
          <TargetGroupHeatmap
            hmkey={targetGroupHeatmapKey}
            allPoiPointsArr={allPoiPointsArr}
          />
        )}

        {/* Poi Tab Markers */}
        <PoiMarkers />

        {/* Data Layer Markers */}
        <DataLayerMarkers cityId={selectedCityId} />

        {/* Selected preview and Show media markers btn */}
        <SelectedPreviewAndShowMediaButton
          showStaticMediaMarkers={showStaticMediaMarkers}
          setShowStaticMediaMarkers={setShowStaticMediaMarkers}
          showDigitalMediaMarkers={showDigitalMediaMarkers}
          setShowDigitalMediaMarkers={setShowDigitalMediaMarkers}
        />

        {/* Map Zoom Indicator */}
        <MapZoomIndicator />
      </LLMap>
    </div>
  );
}
